import { Modal } from "react-bootstrap";
import plusIcon from "../../../../images/plus-solid.png";
import ModifierCategoryModal from "./ModifierCategoryModal.js";
import ProductComboGroup from "./ProductComboGroup.js";
import ErrorAlert from "../../../../common/ui/ErrorAlert.js";
import * as CommonUtilities from "../../../../common/utils/CommonUtils.js";

/* =============================================================================
   Product details — the page the customer actually builds their item on.

   What changed: the photo was a small thumbnail in a 4/8 grid with the whole
   option list crammed into the remaining 8 columns. It is now a full-width
   hero, the name and price sit beneath it, and the options get the full width.
   The quantity stepper and Add to Cart are pinned to the bottom so they stay
   reachable on a long combo instead of sitting below everything.

   ⚠⚠ THE THREE-CONDITION GATE IS LOAD-BEARING AND APPEARS THREE TIMES:
        currentLocation.AllowOnlineOrder
     && currentLocation.Validation.IsValidTableNumber
     && !productDetails.isSoldOut
   It hides special instructions, the quantity stepper and Add to Cart together.
   Dropping any one of the three lets a customer add an item from a closed
   store, a sold-out item, or an item with no valid table in table service.
   All three are reproduced below exactly as they were.

   ⚠ The error alert deliberately sits OUTSIDE that gate — a failure must still
   be visible when the gate has closed.

   ⚠ The energyCount emptiness check compares against 0, "0", "" and null
   separately. That is not redundant: the API returns all four for "no value".

   ⚠ txtareaSpecialInstr and name="SpecialInstructions" are read by
   handleProductFormChange by name. Do not rename either.
   ============================================================================= */

export default function ProductDetailsModalPage(props) {
    let instrIsInvalid = "";
    if (props.productFormData.validation.SpecialInstructions.HasError) {
        instrIsInvalid = "is-invalid";
    } else {
        instrIsInvalid = "";
    }

    let energyCount;
    if (props.productDetails.energyCount !== 0 && props.productDetails.energyCount !== "0" && props.productDetails.energyCount !== "" && props.productDetails.energyCount !== null) {
        energyCount = <span className="wo-pm__kj">{props.productDetails.energyCount}kJ</span>
    } else {
        energyCount = <></>
    }
    let specialInstructions, titleSpecialInstructions, charLengthSpecialInstructions;
    if (props.productFormData.config.SpecialInstructions.IsShownMod) {
        titleSpecialInstructions = props.productFormData.config.SpecialInstructions.Title;
        charLengthSpecialInstructions = props.productFormData.config.SpecialInstructions.CharacterLength;
    } else if (props.productFormData.config.SpecialInstructions.IsShownProduct) {
        titleSpecialInstructions = props.productFormData.config.ProductSpecialInstructions.ProductTitle;
        charLengthSpecialInstructions = props.productFormData.config.ProductSpecialInstructions.ProductCharacterLength;
    }
    // === begin: Bulk Pricing ===
    /* ⚠ The quantity-break table. Under bulk pricing a product is priced by how
       many you buy, and this card is the ONLY place the customer sees those
       breaks before adding to cart — qtyEndRange 9999 means "and above".

       The redesign deleted it entirely, which would have left bulk-pricing brands
       with a single unit price and no way to see the discount they were about to
       earn. Restored, and the "Discount will be applied in cart" line matters:
       the prices shown here are not what the cart line will say until the
       quantity is in. */
    let priceRangeCard = null;
    if (CommonUtilities.StaticFields.PriceType === 'bp8826'
        && props.productDetails.bulkPricingProductsPriceList
        && props.productDetails.bulkPricingProductsPriceList.length > 0) {
        const qtyRange = props.productDetails.bulkPricingProductsPriceList.map((item, index) => {
            return <small key={index}>{item.qtyStartRange}{item.qtyEndRange === 9999 ? '+' : ' - ' + item.qtyEndRange}</small>
        });
        const bulkPrices = props.productDetails.bulkPricingProductsPriceList.map((item, index) => {
            return <small key={index}>{CommonUtilities.StaticFields.CurrencySymbol}{(item.priceInc).toFixed(2)} each</small>
        });
        priceRangeCard =
            <div className="wo-pm__bulk">
                <div className="wo-pm__bulkGrid">
                    <div className="wo-pm__bulkCol">{qtyRange}</div>
                    <div className="wo-pm__bulkCol">{bulkPrices}</div>
                </div>
                <small className="wo-pm__bulkNote"><i>Discount will be applied in cart</i></small>
            </div>
    }
    // === end: Bulk Pricing ===

    // Regular or Combo Product
    let modifierCategories;
    if (props.productDetails.itemUsageTypeName === "Regular") { // ItemUsageTypeID = 1 (Regular Product)
        modifierCategories = props.productDetails.modCategories?.map((item) => {
            return (
                <ModifierCategoryModal
                key={item.modCategoryId}
                modifierCategoryDetails={item}
                handleModFormChange={props.handleModFormChange}
                handleModifierComboSelect={props.handleModifierComboSelect}
                handleRemoveModifierComboFromProduct={props.handleRemoveModifierComboFromProduct}
                productFormDataMods={props.productFormData.data.Mods} // Passing as productFormDataMods
                productFormDataModCatValidation={props.productFormData.validation.ModCategories} // Passing as productFormDataModCatValidation
                addToCartError={props.addToCartError}
                currentLocation={props.currentLocation}
                priceInc={props.productDetails.priceInc}
                />
            )
        })

        // Special Instructions
        if (props.productFormData.config.SpecialInstructions.IsShownProduct || props.productFormData.config.SpecialInstructions.IsShownMod) {
            specialInstructions = <div className="wo-pm__instr">
                                    <label htmlFor="txtareaSpecialInstr" className="wo-pm__instrLabel">{titleSpecialInstructions}</label>
                                    <textarea className={`form-control wo-pm__instrBox ${instrIsInvalid}`} id="txtareaSpecialInstr" name="SpecialInstructions" rows="2" placeholder="Anything we should know?" value={props.productFormData.data.SpecialInstructions} onChange={props.handleProductFormChange} disabled={props.addToCartError.ButtonsDisabled} maxLength={charLengthSpecialInstructions}></textarea>
                                    <small className="wo-pm__instrHint">{`Up to ${charLengthSpecialInstructions} characters`}</small>
                                    <small className="invalid-feedback">{props.productFormData.validation.SpecialInstructions.ErrorMessage}</small>
                                </div>
        } else {
            specialInstructions = <></>
        }
    } else if (props.productDetails.itemUsageTypeName === "Combo") { // ItemUsageTypeID = 2 (Product Combo)
        modifierCategories = props.productDetails.comboGroups?.map((item) => {
            // Find group form data for this product group
            let groupFormData = props.productFormData.data.ProductsInCombo.find((groupItem) => groupItem.productComboGroupID == item.productComboGroupID);
            let groupValidationData = props.productFormData.validation.ProductComboGroups.find((groupValidation) => groupValidation.ProductComboGroupID == item.productComboGroupID);

            return (
                <ProductComboGroup
                    key={item.productComboGroupID}
                    productComboGroupDetails={item}
                    groupFormData={groupFormData} // productFormData being passed just for groupFormData
                    groupValidationData={groupValidationData}
                    plusIcon={plusIcon}
                    handleProductComboGroupSelect={props.handleProductComboGroupSelect}
                    addToCartError={props.addToCartError}
                />
            )
        })
        specialInstructions = <></>
    }

    // Gate — see the warning at the top of this file. Computed once, used three
    // times, so the three can never drift apart.
    const canOrder = props.currentLocation.AllowOnlineOrder
        && props.currentLocation.Validation.IsValidTableNumber
        && !props.productDetails.isSoldOut;

    // No placeholder graphic any more: a product with no photo simply has no
    // hero, which reads better than a grey box pretending to be one.
    const hasImage = !!props.productDetails.productImageURL;

    /* ⚠ The photo must not bury the choices.
       A full-width hero is 16:9 of a 560px modal — over 300px — which on a
       laptop pushed the first option group off the bottom of a combo. The
       customer saw a lovely picture and no way to tell there was anything to
       choose.

       So: a product with options gets a compact header — thumbnail beside the
       name — and a product with nothing to choose keeps the full hero, because
       there is nothing below it to reach. Same reasoning as the menu card's
       three media states. */
    const optionCount = props.productDetails.itemUsageTypeName === "Combo"
        ? (props.productDetails.comboGroups || []).length
        : (props.productDetails.modCategories || []).length;
    const hasOptions = optionCount > 0;

    return (
        <>
            <Modal.Body className="wo-pm__body">
                {hasImage && !hasOptions &&
                    <div className="wo-pm__hero">
                        <img src={props.productDetails.productImageURL} alt={props.productDetails.productName}
                             loading="lazy" decoding="async" />
                    </div>}

                <div className={`wo-pm__intro${hasOptions ? ' wo-pm__intro--compact' : ''}`}>
                    {hasImage && hasOptions &&
                        <span className="wo-pm__thumb">
                            <img src={props.productDetails.productImageURL} alt={props.productDetails.productName}
                                 loading="lazy" decoding="async" />
                        </span>}
                    <div className="wo-pm__introText">
                        <div className="wo-pm__nameRow">
                            <h3 className="wo-pm__name">{props.productDetails.productName}</h3>
                            {energyCount}
                        </div>
                        {props.productDetails.productDescription &&
                            <p className="wo-pm__desc">{props.productDetails.productDescription}</p>}
                        <p className="wo-pm__price">
                            ${props.productDetails.priceInc && props.productDetails.priceInc.toFixed(2)}
                        </p>
                        {priceRangeCard}
                    </div>
                </div>

                {/* ModifierCategoryModal and ProductComboGroup emit .col-12
                    children, so this wrapper keeps a grid row for them. */}
                <div className="wo-pm__options row g-0">
                    {modifierCategories}
                </div>

                {canOrder && specialInstructions}
            </Modal.Body>

            <Modal.Footer className="wo-pm__foot">
                { props.addToCartError.HasError && <div className="wo-pm__footErr"><ErrorAlert message={props.addToCartError.ErrorMessage} /></div> }

                {!canOrder &&
                    <p className="wo-pm__unavailable">
                        {props.productDetails.isSoldOut ? 'Sold out' : 'Not available right now'}
                    </p>}

                { canOrder &&
                <div className="wo-pm__footBar">
                    <div className="wo-qty">
                        <button type="button" className="wo-qty__btn" onClick={props.handleProductFormSubtractQuantity} disabled={props.addToCartError.ButtonsDisabled} aria-label="Reduce quantity">
                            {/* Drawn in currentColor, not the white PNGs: those were made for
                                the old accent-coloured buttons and vanished on this pale stepper.
                                Not a typed +/- either — those glyphs shift with the brand font. */}
                            <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                        </button>
                        <span className="wo-qty__num">{props.productFormData.data.Quantity}</span>
                        <button type="button" className="wo-qty__btn" onClick={props.handleProductFormAddQuantity} disabled={props.addToCartError.ButtonsDisabled} aria-label="Increase quantity">
                            <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M8 3v10" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
                        </button>
                    </div>

                    <button type="button" className="wo-pm__add" onClick={props.handleAddProductToCart} disabled={props.addToCartError.ButtonsDisabled}>
                        Add to Cart
                        {props.addToCartError.ButtonsDisabled && <>&ensp;<span className="spinner-border spinner-border-sm" aria-hidden="true"></span></>}
                    </button>
                </div>}

                { canOrder && props.productFormData.validation.Quantity.HasError &&
                    <small className="wo-pm__qtyErr">{props.productFormData.validation.Quantity.ErrorMessage}</small>}
            </Modal.Footer>
        </>
    )
}
